import { useState } from 'react'
import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { MediaPicker } from '@/features/designer/components/MediaPicker'
import { FocusPointPicker } from '@/features/designer/components/FocusPointPicker'
import type { MediaDocument } from '@/features/admin/services/admin.media.service'

type Step = 'pick-media' | 'pick-focus'

interface ServicePhotoSheetProps {
  open: boolean
  onClose: () => void
  currentMediaId?: string
  currentFocusX?: number
  currentFocusY?: number
  onAssign: (mediaId: string, mediaUrl: string, focusX: number, focusY: number) => void
}

export function ServicePhotoSheet({
  open,
  onClose,
  currentMediaId,
  currentFocusX = 0.5,
  currentFocusY = 0.5,
  onAssign,
}: ServicePhotoSheetProps) {
  const [step, setStep] = useState<Step>('pick-media')
  const [selectedMedia, setSelectedMedia] = useState<MediaDocument | null>(null)
  const [focusX, setFocusX] = useState(currentFocusX)
  const [focusY, setFocusY] = useState(currentFocusY)

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      onClose()
      setTimeout(() => {
        setStep('pick-media')
        setSelectedMedia(null)
        setFocusX(currentFocusX)
        setFocusY(currentFocusY)
      }, 300)
    }
  }

  function handleNext() {
    if (!selectedMedia) return
    setFocusX(currentFocusX)
    setFocusY(currentFocusY)
    setStep('pick-focus')
  }

  function handleAssign() {
    if (!selectedMedia) return
    onAssign(selectedMedia.id, selectedMedia.url, focusX, focusY)
    onClose()
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex w-[480px] max-w-full flex-col gap-0 p-0 sm:max-w-[480px]">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle className="text-base">Service Photo</SheetTitle>
          <SheetDescription className="text-xs">
            Select a photo to display alongside this service.
          </SheetDescription>
        </SheetHeader>

        {step === 'pick-media' ? (
          <>
            <div className="flex-1 min-h-0 overflow-hidden p-4">
              <MediaPicker
                selectedId={selectedMedia?.id ?? currentMediaId ?? null}
                onSelect={setSelectedMedia}
              />
            </div>
            <div className="border-t px-6 py-4 flex justify-end">
              <Button onClick={handleNext} disabled={!selectedMedia} className="gap-2">
                Set focus point
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6">
              {selectedMedia && (
                <FocusPointPicker
                  imageUrl={selectedMedia.url}
                  focusX={focusX}
                  focusY={focusY}
                  onChange={(x, y) => { setFocusX(x); setFocusY(y) }}
                />
              )}
            </div>
            <div className="border-t px-6 py-4 flex items-center justify-between">
              <Button variant="outline" onClick={() => setStep('pick-media')} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleAssign} className="gap-2">
                <Check className="h-4 w-4" />
                Apply
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
